"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Wind, Zap, Volume2, Gauge, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Factory, Layers, ThermometerSnowflake, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
const benefits = [{
  id: "energy",
  title: "Redução de custos energéticos",
  description: "Os sistemas de lâminas de ar WindJet e sopradores regenerativos consomem até 75% menos energia quando comparados com tubos abertos de ar comprimido tradicional.",
  icon: Zap,
  metric: "Até -75%",
  metricLabel: "consumo energético"
}, {
  id: "noise",
  title: "Controlo do ruído ocupacional",
  description: "O perfil aerodinâmico dos bicos e facas de ar minimiza turbulências severas, reduzindo drasticamente os decibéis na zona de trabalho fabril.",
  icon: Volume2,
  metric: "Até 15 dB(A)",
  metricLabel: "menos ruído"
}, {
  id: "uniformity",
  title: "Impacto uniforme e contínuo",
  description: "Distribuição homogénea de caudal e velocidade a toda a largura da linha de produção, eliminando manchas de humidade e resíduos.",
  icon: Wind,
  metric: "100%",
  metricLabel: "cobertura contínua"
}, {
  id: "pressure",
  title: "Pressão e caudal calibrados",
  description: "Sistemas desenhados à medida do produto com ar de baixo ruído e baixa pressão de alimentação, preservando a integridade das embalagens e peças.",
  icon: Gauge,
  metric: "Ótimo",
  metricLabel: "rendimento mecânico"
}];
const useCases = [{
  id: "bottling",
  category: "Engarrafamento & Conservas",
  title: "Secagem pré-rotulagem e embalamento",
  description: "Elimina películas de água do gargalo, tampas e corpo de garrafas ou latas antes da rotulagem, codificação laser ou empacotamento em cartão.",
  features: ["Secagem completa de coroas e reentrâncias", "Eliminação de erros de adesão nos rótulos", "Compatibilidade com linhas de alta cadência"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/drying_bottling_line.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/drying_bottling_line.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/drying_bottling_line.png-wvc-sizes",
    alt: "Linha de secagem e blow-off em garrafas"
  }
}, {
  id: "metals",
  category: "Metalomecânica & Peças",
  title: "Remoção de óleos, aparas e água de lavagem",
  description: "Sopragem direcionada de elevada força de impacto para desengordurar, secar perfis metálicos, remover limalhas ou arrefecer peças tratadas termicamente.",
  features: ["Desobstrução de orifícios e canais cegos", "Secagem rápida pós-lavagem por imersão", "Ar isento de contaminações de óleo de compressor"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/drying_metal_parts.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/drying_metal_parts.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/drying_metal_parts.webp-wvc-sizes",
    alt: "Remoção de resíduos e sopragem em peças metálicas"
  }
}, {
  id: "food",
  category: "Indústria Alimentar",
  title: "Blow-off higiénico e arrefecimento controlado",
  description: "Facas de ar e bicos com certificação alimentar para retirar excesso de água em vegetais lavados, remover migalhas em padaria ou arrefecer produtos cozinhados.",
  features: ["Construção em aço inoxidável 316 e materiais FDA", "Barreira sanitária contra contaminação cruzada", "Pressão suave que não danifica alimentos frágeis"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/drying_food_processing.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/drying_food_processing.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/drying_food_processing.webp-wvc-sizes",
    alt: "Secagem e blow-off de produtos na indústria alimentar"
  }
}];
const specs = [{
  label: "Tecnologia principal",
  value: "Lâminas de Ar WindJet & Bicos de Sopragem"
}, {
  label: "Opções de alimentação",
  value: "Sopradores regenerativos elétricos ou ar comprimido otimizado"
}, {
  label: "Materiais disponíveis",
  value: "Inox 303, Inox 316L, Alumínio Anodizado e PVDF"
}, {
  label: "Eficiência acústica",
  value: "Conforme normas OSHA de ruído industrial"
}];
export default function DryingOverview() {
  const [activeTab, setActiveTab] = useState("bottling");
  return <section id="dryingblowoffoverview" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground transition-colors overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 border-secondary/30 text-secondary bg-secondary/10">
            Sistemas de Engenharia de Ar
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Secagem e sopragem de precisão para linhas industriais
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            A Eurocontrol fornece as soluções avançadas da Spraying Systems Co. para substituir o uso dispendioso de ar comprimido tradicional por sistemas de lâminas e bicos com alto rendimento dinâmico.
          </p>
        </div>

        {/* Technical Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 lg:mb-20">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <motion.div key={benefit.id} data-index={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="h-full">
                <Card className="h-full border border-border/80 shadow-sm bg-card hover:border-secondary/40 transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="space-y-3 pb-2">
                    <div className="flex items-center justify-between">
                      <div className="size-11 rounded-lg bg-accent text-secondary flex items-center justify-center">
                        <Icon className="size-6" />
                      </div>
                      <Badge variant="secondary" className="font-mono font-semibold">
                        {benefit.metric}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground">
                      {benefit.title}
                    </CardTitle>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {benefit.metricLabel}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Detailed Use Cases Interactive Tabs */}
        <div className="bg-muted/40 border border-border rounded-2xl p-6 sm:p-8 lg:p-10 mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <span className="text-xs font-bold tracking-widest text-secondary uppercase block mb-1">
                Aplicações em Fábrica
              </span>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                Casos práticos de secagem e blow-off
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Configurações concebidas para garantir repetibilidade operacional e eliminar desperdícios nas mais diversas indústrias.
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-1 sm:grid-cols-3 w-full h-auto p-1.5 bg-background/80 border border-border mb-8 gap-1.5">
              {useCases.map((uc, i) => <TabsTrigger key={uc.id} value={uc.id} data-index={i} className="py-2.5 px-4 text-xs sm:text-sm font-medium rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-[color,background-color]">
                  {uc.category}
                </TabsTrigger>)}
            </TabsList>

            {useCases.map((uc, i) => <TabsContent key={uc.id} value={uc.id} data-index={i} className="focus-visible:outline-none">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-6 space-y-5">
                    <div>
                      <Badge variant="outline" className="mb-2 text-primary border-primary/20 bg-primary/5">
                        {uc.category}
                      </Badge>
                      <h4 className="text-2xl font-bold font-default text-foreground tracking-tight">
                        {uc.title}
                      </h4>
                    </div>

                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {uc.description}
                    </p>

                    <div className="space-y-2.5 pt-2">
                      {uc.features.map((feature, featureIndex) => <div key={featureIndex} data-index={featureIndex} className="flex items-start gap-3">
                          <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground/90 font-medium">
                            {feature}
                          </span>
                        </div>)}
                    </div>

                    <div className="pt-4 flex flex-wrap items-center gap-4">
                      <Button asChild variant="default" size="default" className="gap-2">
                        <Link to="/ContactsPage">
                          Solicitar dimensionamento técnico
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="default">
                        <Link to="/AirNozzlesAndBlowingProductsPage">
                          Ver bicos e lâminas de ar
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <div className="lg:col-span-6">
                    <div className="relative rounded-xl overflow-hidden border border-border shadow-md aspect-4/3 bg-muted">
                      <img src={uc.image.src} data-wvc-srcset={uc.image.srcset} data-wvc-sizes={uc.image.sizes} alt={uc.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent flex items-end p-6">
                        <div className="text-white space-y-1">
                          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/90">
                            <ShieldCheck className="size-4 text-emerald-400" />
                            Garantia Spraying Systems Co.
                          </div>
                          <p className="text-sm text-white/80">
                            Equipamentos instalados e comissionados pela equipa técnica Eurocontrol
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Technical Highlights & CTA Strip */}
        <div className="border border-border/70 rounded-xl bg-card p-6 sm:p-8 shadow-xs">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {specs.map((item, i) => <div key={item.label} data-index={i} className="space-y-1.5 border-l-2 border-secondary pl-4">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {item.label}
                </span>
                <p className="text-sm font-medium text-foreground">
                  {item.value}
                </p>
              </div>)}
          </div>

          <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-sm text-muted-foreground text-center sm:text-left">
              <Sparkles className="size-5 text-secondary shrink-0" />
              <span>
                Auditorias energéticas a sistemas de ar comprimido e testes práticos em fábrica.
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Button asChild variant="secondary">
                <Link to="/TechnicalSolutionsPage">
                  Explorar soluções técnicas
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}